import { api } from '@/api/index.ts'
import type { FetchParams, ParamType } from '@/api/types.ts'

export abstract class BaseApi<
  GetReturn,
  FetchReturn,
  Input,
  FetchDefaultParams = FetchParams,
> {
  abstract readonly baseUrl: string
  readonly config = {}

  protected getBaseUrl(urlTemplate: string, params: ParamType[] = []): string {
    let url = urlTemplate

    const matches = [...url.matchAll(/:(\w+)/g)]

    matches.forEach((match, index) => {
      const [placeholder] = match
      const paramValue = params[index]
      if (paramValue !== undefined) {
        url = url.replace(placeholder, String(paramValue))
      }
    })

    return url
  }

  protected toParamArray(id: ParamType | ParamType[]): ParamType[] {
    return Array.isArray(id) ? id : [id]
  }

  buildFetchParams(queryParams: FetchDefaultParams): Record<string, string> {
    const filteredParams: Record<string, string> = {}

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (value == null) return

      if (Array.isArray(value)) {
        filteredParams[key] = value.join(',')
      } else if (typeof value === 'object') {
        filteredParams[key] = JSON.stringify(value)
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        filteredParams[key] = String(value)
      }
    })

    return filteredParams
  }

  async get(id: ParamType | ParamType[]): Promise<GetReturn> {
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:id`, [...params])
    const { data } = await api.get<GetReturn>(url, this.config)
    return data
  }

  async fetch(
    params: ParamType[],
    queryParams: FetchDefaultParams = {} as FetchDefaultParams
  ): Promise<FetchReturn> {
    const filteredParams: Record<string, string> =
      this.buildFetchParams(queryParams)

    const URParams = new URLSearchParams(filteredParams)
    const url = `${this.getBaseUrl(this.baseUrl, params)}?${URParams.toString().replaceAll('+', '%20')}`
    const { data } = await api.get<FetchReturn>(url, this.config)
    return data
  }

  async update(id: ParamType | ParamType[], payload: Input): Promise<unknown> {
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:id`, params)
    const { data } = await api.put<unknown>(url, payload, this.config)
    return data
  }

  async create(params: ParamType[], payload: Input): Promise<unknown> {
    const url = this.getBaseUrl(`${this.baseUrl}`, params)
    const { data } = await api.post<unknown>(url, payload, this.config)
    return data
  }

  async delete(id: ParamType | ParamType[]): Promise<void> {
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:id`, params)
    const { data } = await api.delete<void>(url, this.config)
    return data
  }

  async patch(
    id: ParamType | ParamType[],
    payload: Partial<Input>
  ): Promise<unknown> {
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:id`, params)
    const { data } = await api.patch<unknown>(url, payload, this.config)
    return data
  }
}
